import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";

const DEFAULT_META = {
  title: "Quick Apply | Online Application for Business Accounts and Business Finance | RAKBANK",
  description:
    "Apply online for a RAKBANK business account in the UAE. Complete your application in minutes and get access to digital banking, trade finance and business financing solutions.",
  canonicalUrl: "https://quickapply.rakbank.ae/digitalbank/",
  keywords:
    "rakbank business account, business banking uae, open business account online, rakbank quick apply, rakstarter account",
  image: "https://quickapply.rakbank.ae/digitalbank/assets/seo/og-image.svg"
};

const DEFAULT_STRUCTURED_DATA = [
  {
    "@context": "https://schema.org",
    "@type": "FinancialService",
    name: "RAKBANK Quick Apply",
    description:
      "Digital onboarding for RAKBANK business banking customers in the United Arab Emirates.",
    url: "https://quickapply.rakbank.ae/digitalbank/",
    areaServed: {
      "@type": "Country",
      name: "United Arab Emirates"
    },
    provider: {
      "@type": "BankOrCreditUnion",
      name: "RAKBANK",
      url: "https://www.rakbank.ae",
      logo: "https://quickapply.rakbank.ae/digitalbank/assets/seo/og-image.svg",
      sameAs: [
        "https://www.facebook.com/RAKBANK",
        "https://www.linkedin.com/company/rakbank",
        "https://www.youtube.com/user/RAKBANKlive"
      ]
    }
  }
];

const INDEXABLE_PATHS = [
  /^\/$/,
  /^\/digitalbank\/?$/,
  /^\/digitalbank\/business\/?$/,
  /^\/business\/?$/,
  /^\/business\/digitalbank\/?$/
];

/**
 * Renders baseline SEO tags without modifying the visible application UI.
 * Allows individual screens to override metadata for specialised campaigns.
 */
export const SEOManager = ({
  title,
  description,
  canonicalUrl,
  keywords,
  image,
  noIndex = false,
  structuredData
}) => {
  // Check if we're in a browser environment using globalThis
  const globalObj = (function() {
    // eslint-disable-next-line no-undef
    if (typeof globalThis !== "undefined") return globalThis;
    // eslint-disable-next-line no-undef
    /* istanbul ignore next */
    if (typeof window !== "undefined") return window; // NOSONAR
    /* istanbul ignore next */
    return {};
  })();
  const isBrowser = typeof globalObj.location !== "undefined"; // NOSONAR

  const runtimePath = isBrowser && globalObj.location ? globalObj.location.pathname : "/";

  const resolvedCanonicalUrl = useMemo(() => {
    if (canonicalUrl) {
      return canonicalUrl;
    }

    if (isBrowser && globalObj.location) {
      const url = new URL(globalObj.location.href);
      url.hash = "";
      return url.href;
    }

    return DEFAULT_META.canonicalUrl;
  }, [canonicalUrl]);

  const resolvedStructuredData = useMemo(() => {
    if (structuredData && structuredData.length > 0) {
      return structuredData;
    }

    /* istanbul ignore next */
    return DEFAULT_STRUCTURED_DATA;
  }, [structuredData]);

  const shouldIndex = useMemo(() => INDEXABLE_PATHS.some(pattern => pattern.test(runtimePath)), [
    runtimePath
  ]);

  const robotsValue = useMemo(() => {
    if (noIndex === undefined) {
      /* istanbul ignore next */
      return shouldIndex ? "index,follow" : "noindex,nofollow";
    }

    return noIndex ? "noindex,nofollow" : "index,follow";
  }, [noIndex, shouldIndex]);

  const metaTitle = title || DEFAULT_META.title;
  const metaDescription = description || DEFAULT_META.description;
  const metaKeywords = keywords || DEFAULT_META.keywords;
  const metaImage = image || DEFAULT_META.image;

  return (
    <Helmet>
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <meta name="robots" content={robotsValue} />
      <link rel="canonical" href={resolvedCanonicalUrl} />
      <meta property="og:locale" content="en_AE" />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="RAKBANK Quick Apply" />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={resolvedCanonicalUrl} />
      <meta property="og:image" content={metaImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />
      {resolvedStructuredData.map(data => (
        <script
          key={`structured-data-${data["@type"]}-${data.name || data.url}`}
          type="application/ld+json"
        >
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};

SEOManager.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  canonicalUrl: PropTypes.string,
  keywords: PropTypes.string,
  image: PropTypes.string,
  noIndex: PropTypes.bool,
  structuredData: PropTypes.arrayOf(PropTypes.object)
};
